import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { cn } from "@/utils/cn";

export type AutoFormulateMode = "loss" | "cost" | "prompt";

export type AutoFormulateRunOpts = {
  mode: AutoFormulateMode;
  prompt?: string;
};

type AutoFormulateButtonProps = {
  disabled?: boolean;
  /** When `disabled` is true, surface this as the hover tooltip explaining
   *  why. Rendered as a native `title` on the OUTER wrapper because the
   *  inner `.af-btn-group.is-disabled` carries `pointer-events: none` —
   *  the inner buttons can't fire hover events. */
  disabledReason?: string;
  onRun: (opts: AutoFormulateRunOpts) => void;
};

// Module-level read of the deployment-tier env var. Default (unset or any
// value other than the literal string "true") is PILOT, in which BOTH
// "Optimize for cost" and "From prompt…" surface as PRO-tier locked rows.
// Toggling a deployment to PRO is a single env-var flip + rebuild.
//
// Future migration to per-org tiering (Clerk metadata, feature-flag
// service) will replace this single line.
const IS_PRO = import.meta.env.VITE_NF_AF_PRO_MODES === "true";

// Split button that drives Auto-Formulate. Body click runs the default mode
// (Optimize Loss — always available, including on PILOT). Caret opens a
// small mode menu (loss / cost / from prompt). On PILOT (IS_PRO=false),
// "Optimize for cost" and "From prompt…" surface as PRO-tier locked rows —
// visible so users discover them, but inert with a warm PRO pill, and the
// menu's footer strip carries a single "More modes available on PRO" hint.
export function AutoFormulateButton({
  disabled = false,
  disabledReason,
  onRun,
}: AutoFormulateButtonProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (!open && !promptOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPromptOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, promptOpen]);

  const runLoss = () => {
    setOpen(false);
    onRun({ mode: "loss" });
  };
  const runCost = () => {
    if (!IS_PRO) return;
    setOpen(false);
    onRun({ mode: "cost" });
  };
  const openPromptDialog = () => {
    if (!IS_PRO) return;
    setOpen(false);
    setPromptOpen(true);
  };
  const submitPrompt = () => {
    setPromptOpen(false);
    onRun({
      mode: "prompt",
      prompt: prompt.trim() || "tone down dairy, lift fresh-green top notes",
    });
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex" }}
      // When disabled, the inner group has pointer-events:none so the
      // button-level title never fires. The wrapper keeps default
      // pointer-events, so its title is the reliable place for the
      // "why is this disabled" tooltip.
      title={disabled ? disabledReason : undefined}
    >
      <div className={cn("af-btn-group", disabled && "is-disabled")}>
        <button
          type="button"
          className="af-btn-main"
          disabled={disabled}
          onClick={runLoss}
          title={disabled ? undefined : "Auto-Formulate (Optimize Loss)"}
        >
          <span className="af-spark" aria-hidden="true">
            <svg viewBox="0 0 12 12" width="12" height="12">
              <path
                d="M6 1 L7.2 4.8 L11 6 L7.2 7.2 L6 11 L4.8 7.2 L1 6 L4.8 4.8 Z"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </span>
          <span>Auto-Formulate</span>
        </button>
        <button
          type="button"
          className="af-btn-caret"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-label="Auto-Formulate options"
          title="Choose mode"
        >
          <span style={{ fontSize: 9 }}>▾</span>
        </button>
      </div>

      {open && (
        <div
          className="wb-pop af-menu"
          style={{ width: 280, top: "calc(100% + 7px)", right: 0 }}
        >
          <div className="wb-pop-header">
            <span className="label">Auto-Formulate mode</span>
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
              v2.3.1
            </span>
          </div>
          <div className="vstack" style={{ padding: 6, gap: 1 }}>
            <button type="button" className="af-menu-item" onClick={runLoss}>
              <span className="af-menu-icon" style={{ color: "var(--brand-green)" }}>
                <Icon name="target" size={13} />
              </span>
              <div className="vstack" style={{ gap: 2, lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, color: "var(--ink)", fontWeight: 500 }}>
                  Optimize loss
                </span>
                <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                  Minimize sensory distance to target
                </span>
              </div>
              <span className="kbd" style={{ fontSize: 9 }}>⌘⏎</span>
            </button>

            <button
              type="button"
              className={cn("af-menu-item", !IS_PRO && "is-locked")}
              onClick={runCost}
              disabled={!IS_PRO}
              aria-disabled={!IS_PRO}
              title={
                IS_PRO
                  ? "Optimize for cost"
                  : "Available on PRO"
              }
            >
              <span
                className="af-menu-icon"
                style={{ color: IS_PRO ? "var(--brand-amber)" : "var(--ink-4)" }}
              >
                <Icon name="zap" size={13} />
              </span>
              <div className="vstack" style={{ gap: 2, lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    fontSize: 12.5,
                    color: IS_PRO ? "var(--ink)" : "var(--ink-3)",
                    fontWeight: 500,
                  }}
                >
                  Optimize for cost
                </span>
                <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                  Hold loss; reduce $ /100g
                </span>
              </div>
              {!IS_PRO && (
                <span className="af-plan-tag is-pro" aria-label="PRO tier">
                  <Icon name="sparkle" size={10} />
                  <span>PRO</span>
                </span>
              )}
            </button>

            <div
              style={{
                height: 1,
                background: "var(--line-2)",
                margin: "4px 6px",
              }}
            />

            <button
              type="button"
              className={cn("af-menu-item", !IS_PRO && "is-locked")}
              onClick={openPromptDialog}
              disabled={!IS_PRO}
              aria-disabled={!IS_PRO}
              title={
                IS_PRO
                  ? "Steer with a natural-language prompt"
                  : "Available on PRO"
              }
            >
              <span
                className="af-menu-icon"
                style={{
                  color: IS_PRO ? "var(--brand-green)" : "var(--ink-4)",
                }}
              >
                <Icon name="text" size={13} />
              </span>
              <div className="vstack" style={{ gap: 2, lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    fontSize: 12.5,
                    color: IS_PRO ? "var(--ink)" : "var(--ink-3)",
                    fontWeight: 500,
                  }}
                >
                  From prompt…
                </span>
                <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                  Free-text steering · taste, mouthfeel, claims
                </span>
              </div>
              {!IS_PRO && (
                <span className="af-plan-tag is-pro" aria-label="PRO tier">
                  <Icon name="sparkle" size={10} />
                  <span>PRO</span>
                </span>
              )}
            </button>
          </div>

          {!IS_PRO && (
            <>
              <div className="af-menu-divider" aria-hidden="true" />
              <div className="af-menu-foot" aria-hidden="true">
                <Icon name="sparkle" size={9} />
                <span>More modes available on PRO</span>
              </div>
            </>
          )}
        </div>
      )}

      {promptOpen && (
        <div
          className="wb-pop af-menu"
          style={{ width: 380, top: "calc(100% + 7px)", right: 0 }}
        >
          <div className="wb-pop-header">
            <span className="label">Auto-Formulate from prompt</span>
            <span className="mono" style={{ fontSize: 9, color: "var(--brand-green)" }}>
              ENTERPRISE
            </span>
          </div>
          <div className="vstack" style={{ padding: 12, gap: 10 }}>
            <span style={{ fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.45 }}>
              Describe how the formulation should shift. The AI keeps your foundation
              locked and edits only the Flavor side.
            </span>
            <textarea
              autoFocus
              rows={4}
              placeholder="e.g. Push fresh-green top notes, soften the dairy lactones, keep budget under $42/100g"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="mono"
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 4,
                border: "1px solid var(--line)",
                background: "var(--bg)",
                fontSize: 11.5,
                color: "var(--ink)",
                resize: "vertical",
                lineHeight: 1.4,
              }}
            />
            <div className="hstack" style={{ justifyContent: "flex-end", gap: 6 }}>
              <button
                type="button"
                className="btn xs ghost"
                onClick={() => setPromptOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="btn xs primary" onClick={submitPrompt}>
                <Icon name="sparkle" size={11} />
                Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
